import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { userApi } from "api/user/user";
import Loading from "components/lodingState/loading";
import ForgetPassword from "components/login/forgetPassword/forgetPassword";
import { useFormik } from "formik";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BtnStyle } from "styles/buttonStyle";
import { snackBarStyle } from "styles/snackBarStyle";
import { textFieldStyles } from "styles/textFieldStyles";
import * as Yup from "yup";

const ResetPassPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const navigate = useNavigate();
  const [toggleForgetPassForm, setToggleForgetPassForm] = useState(false);

  const handleForgetPassForm = () => {
    setToggleForgetPassForm(!toggleForgetPassForm);
  };

  const {
    data: tokenQuery,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["validateResetToken", token, email],
    queryFn: () => userApi.validateResetToken(token, email),
    enabled: !!token,
  });

  const mutation = useMutation({
    mutationFn: userApi.resetPassword,
    mutationKey: "reset password",
    onSuccess: (response) => {
      if (response.status === 200) {
        enqueueSnackbar("Adgangskode blev ændret!", snackBarStyle());
        navigate("/", { state: { openModal: true } });
        formik.resetForm();
      } else {
        enqueueSnackbar(
          "Adgangskode skal minimum være 8 karakterer. Adgangskode skal indeholde et lille bogstav. Adgangskode skal indeholde et stort bogstav. Adgangskode skal indeholde et tal. Adgangskode skal indeholde et symbol.",
          snackBarStyle("error", 3000)
        );
      }
    },

    onError: (error) => {
      enqueueSnackbar(
        "Adgangskode skal minimum være 8 karakterer. Adgangskode skal indeholde et lille bogstav. Adgangskode skal indeholde et stort bogstav. Adgangskode skal indeholde et tal. Adgangskode skal indeholde et symbol.",
        snackBarStyle("error", 3000)
      );
    },
  });

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required("Ny adgangskode er påkrævet")
      .min(8, "Adgangskode skal minimum være 8 karakterer")
      .matches(/[A-Z]/, "Adgangskode skal indeholde et lille bogstav")
      .matches(/[a-z]/, "Adgangskode skal indeholde et stort bogstav")
      .matches(/\d/, "Adgangskode skal indeholde et tal")
      .matches(
        /[!@#$%^&*()_+{}\[\]:;"'<>,.?~\\/-]/,
        "Adgangskode skal indeholde et symbol"
      ),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Kodeordene er ikke ens")
      .required("Confirm password is required"),
  });

  const formik = useFormik({
    initialValues: {
      password: "",
      confirmPassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values, { resetForm }) => {
      mutation.mutate({ password: values.password, email, token });
      resetForm();
    },
  });

  if (isLoading) return <Loading />;

  if (isError) return <div>Error: {error.message}</div>;

  return (
    <>
      {tokenQuery?.data?.message === "Token is valid" ? (
        <Box
          component="main"
          sx={{
            height: "100vh",
            width: 1,
            position: "fixed",
            top: 0,
            left: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            component={"div"}
            sx={{
              height: "100vh",
              width: 1,
              position: "fixed",
              top: 0,
              left: 0,
              bgcolor: "black",
              zIndex: -10,
              opacity: "0",
            }}
          ></Box>
          <Box
            component={"div"}
            sx={{
              bgcolor: "white",
              p: { xs: 3, sm: 4 },
              width: { xs: "80%", sm: "440px" },
              maxWidth: "440px",
              boxShadow: 1,
            }}
          >
            <Typography variant="h6" component="h2" mb={3} textAlign={"center"}>
              Glemt din adgangskode
            </Typography>
            <Box
              component="form"
              noValidate
              onSubmit={formik.handleSubmit}
              sx={{
                mt: 1,
                width: 1,
              }}
            >
              <Box mb={2} position={"relative"}>
                <TextField
                  fullWidth
                  id="password"
                  label="Nyt kodeord"
                  name="password"
                  autoComplete="password"
                  type="password"
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.password && Boolean(formik.errors.password)
                  }
                  sx={textFieldStyles}
                />
                {formik.touched.password && Boolean(formik.errors.password) && (
                  <Typography
                    component={"p"}
                    mt={0.5}
                    position={"absolut"}
                    fontSize={12}
                    color={"error"}
                  >
                    {formik.errors.password}
                  </Typography>
                )}
              </Box>
              <Box mb={2} position={"relative"}>
                <TextField
                  required
                  fullWidth
                  id="confirmPassword"
                  label="Bekræft kodeord"
                  name="confirmPassword"
                  autoComplete="confirmPassword"
                  type="password"
                  value={formik.values.confirmPassword}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.confirmPassword &&
                    Boolean(formik.errors.confirmPassword)
                  }
                  sx={textFieldStyles}
                />
                {formik.touched.confirmPassword &&
                  Boolean(formik.errors.confirmPassword) && (
                    <Typography
                      position={"absolute"}
                      component={"p"}
                      mt={0.5}
                      fontSize={12}
                      color={"error"}
                    >
                      {formik.errors.confirmPassword}
                    </Typography>
                  )}
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{
                  mt: 2,
                  mb: 2,
                  height: "50px",
                  bgcolor: "#424242",
                  "&:hover": {
                    bgcolor: "#686868",
                  },
                }}
              >
                {mutation.isPending ? (
                  <CircularProgress sx={{ color: "white" }} size={"25px"} />
                ) : (
                  "Nulstil"
                )}
              </Button>
            </Box>
          </Box>
        </Box>
      ) : (
        <Box
          fullWidth
          sx={{
            height: "90vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >
          <PriorityHighIcon
            sx={{ fontSize: "50px", mb: "10px", color: "#b7ab90" }}
          />
          <Typography
            variant="h6"
            textAlign={"center"}
            padding={2}
            sx={{ fontSize: { xs: "15px", sm: "17px" } }}
          >
            Dette link er udløbet eller er ugyldigt. Anmod om et nyt link til
            nulstilling af adgangskode.
          </Typography>
          <Box sx={{ textAlign: "center" }}>
            <Button
              sx={{
                ...BtnStyle,
                fontSize: { xs: "14px", sm: "16px" },
                color: "#b7ab90",
              }}
              onClick={handleForgetPassForm}
            >
              klik her for at sende linket igen
            </Button>
          </Box>
          <ForgetPassword
            display={toggleForgetPassForm}
            onClose={handleForgetPassForm}
            backToLogin={() => {
              setToggleForgetPassForm(false);
            }}
          />
        </Box>
      )}
    </>
  );
};

export default ResetPassPage;
